import { useCallback, useState } from "react";

import { useEuerVatTaxAccountCheckLazyQuery } from "../../../../../../../../api/graphql/queries/euerDeclaration/euerVatTaxAccountCheck.generated";

interface CheckedFor {
  email: string;
  taxYear: number;
}

/**
 * One lazy query shared by the EÜR lines 186 and 141. Nothing is requested
 * until `check` is called; every call asks the tax office again.
 *
 * The modal stays mounted while it is closed, so the hook remembers which
 * customer and year the last check was started for. A result, error or
 * loading state is only exposed while the current inputs still match;
 * otherwise the hook behaves as "not checked yet".
 */
export const useEuerVatTaxAccountCheck = (email: string, taxYear: number) => {
  // "no-cache": the tax account data is never stored in the Apollo cache.
  const [execute, { called, loading, data, error }] =
    useEuerVatTaxAccountCheckLazyQuery({ fetchPolicy: "no-cache" });
  const [checkedFor, setCheckedFor] = useState<CheckedFor | null>(null);

  const check = useCallback(() => {
    setCheckedFor({ email, taxYear });
    // The error is rendered by the component; do not leave a rejected promise.
    execute({ variables: { email, year: taxYear } }).catch(() => undefined);
  }, [execute, email, taxYear]);

  const isCurrent =
    checkedFor !== null &&
    checkedFor.email === email &&
    checkedFor.taxYear === taxYear;

  return {
    check,
    called: isCurrent && called,
    loading: isCurrent && loading,
    error: isCurrent ? error : undefined,
    result: isCurrent ? data?.euerVatTaxAccountCheck : undefined,
  };
};

export type EuerVatTaxAccountCheckState = ReturnType<
  typeof useEuerVatTaxAccountCheck
>;
